import { useEffect, useState } from 'react'
import { styled } from '@linaria/react'
import { reconnectLiveProjections, useProjectionHealth } from '../lib/projectionHealth'

/**
 * Says so when the live read path is down.
 *
 * Every screen in this app reads a projection: the server sweeps changed rows
 * and pushes them over one WebSocket, and the UI redraws itself. When that
 * stream dies the screens do not go blank — they keep rendering the last
 * snapshot, which looks exactly like "nothing has happened since". That is the
 * shape of most "it didn't update" reports: the chef's new order, the
 * customer's reply, the schedule someone just saved.
 *
 * The transport stops retrying after a budget of consecutive failures (a
 * rejected upgrade is indistinguishable from an outage in a browser, so it must
 * not loop forever) — which is why this offers the retry, not just the notice.
 */
const GRACE_MS = 4000

const Bar = styled.div`
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: max(16px, env(safe-area-inset-bottom, 0px));
  z-index: 9998;
  display: flex;
  align-items: center;
  gap: 14px;
  max-width: calc(100vw - 24px);
  padding: 12px 14px 12px 18px;
  border-radius: 999px;
  background: #24160f;
  color: #fff;
  box-shadow: 0 12px 32px -8px rgba(20, 10, 5, 0.5);
  font-size: 0.875rem;
`

const Dot = styled.span`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f0b429;
`

const Text = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Retry = styled.button`
  flex-shrink: 0;
  border: 0;
  cursor: pointer;
  padding: 7px 16px;
  border-radius: 999px;
  background: #fff;
  color: #24160f;
  font: inherit;
  font-weight: 600;

  &:hover {
    filter: brightness(0.94);
  }
`

export const LiveStatusBanner = () => {
  const { phase, gaveUp } = useProjectionHealth()
  // A blink between sockets is not news. Only a drop that outlasts the grace
  // window is worth interrupting someone for.
  const down = phase === 'reconnecting' || phase === 'offline'
  const [settled, setSettled] = useState(false)

  useEffect(() => {
    if (!down) {
      setSettled(false)
      return undefined
    }
    const timer = window.setTimeout(() => setSettled(true), GRACE_MS)
    return () => window.clearTimeout(timer)
  }, [down])

  if (!down || !settled) return null
  return (
    <Bar role="status" aria-live="polite">
      <Dot />
      <Text>
        {gaveUp
          ? 'Live updates are off — this page may be out of date.'
          : 'Reconnecting to live updates…'}
      </Text>
      {gaveUp && (
        <Retry type="button" onClick={reconnectLiveProjections}>
          Reconnect
        </Retry>
      )}
    </Bar>
  )
}
